/**
 * Exponential backoff for MQTT non-terminal connection errors.
 *
 * mqtt.js's `reconnectPeriod` is fixed; we mutate it on the live
 * client to back off when consecutive errors fire, and reset it on
 * a clean connect. Without this, a flaky shopper on hotel wifi
 * would burn the broker's authorizer Lambda every 2s during
 * sustained packet loss.
 *
 * Same shape used by both `useChatConnection` (CRM) and the portal
 * `LiveBody` MQTT effect — pulled here so the math is one place.
 */

export const MQTT_BASE_RECONNECT_MS = 2000;
export const MQTT_MAX_RECONNECT_MS = 30_000;

/**
 * Compute the next `reconnectPeriod` for a given run of consecutive
 * errors. Caps at 30s after ~5 errors. Resets to the base period on
 * a successful connect (caller is responsible for resetting their
 * counter and writing the base back).
 */
export function nextReconnectMs(consecutiveErrors: number): number {
	const exp = Math.min(consecutiveErrors, 5);
	return Math.min(MQTT_MAX_RECONNECT_MS, MQTT_BASE_RECONNECT_MS * 2 ** exp);
}

/**
 * MQTT 5 CONNACK reason codes that indicate the broker has refused
 * the connection for credentials / authorization reasons. Reconnects
 * with the same credentials would just thrash — callers should end()
 * the client and let React recreate it on the next token rotation.
 */
const TERMINAL_CODES = new Set<number>([4, 5, 0x86, 0x87, 0x88, 0x8a]);

export function isTerminalConnectError(
	err: { code?: number } | undefined,
): boolean {
	const code = err?.code;
	return typeof code === 'number' && TERMINAL_CODES.has(code);
}
